import { Box, Stack, Typography } from '@mui/material'
import cls from 'classnames'
import { colord } from 'colord'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from 'react-query'

import { useLiquidityChart } from '@/providers/liquidity-chart'
import { TokenPair } from '@/providers/liquidity-chart/candle-utils'
import { getTickers, secondsToTickerTimeFrame, tickerTimeFrameToSeconds } from '@/utils/apis/tickers'
import { format } from '@/utils/numeric'

import NumericOutput from '../base/NumericOutput'
import { PriceChartProps } from './types'
import usePriceSocket from './use-price-socket'
import { getQueryKeyTickers, getQueryOptionsTickers, mergeOrStartNewCandle, orderTokensLikeBE } from './utils'

const SpanOption = ({
  active,
  color,
  onClick,
  text,
}: {
  active: boolean
  color: string
  onClick: () => void
  text: string
}) => (
  <Typography
    className="px-2 py-1 rounded-lg cursor-pointer text-sm"
    data-is-option-active={active}
    data-testid={`span-option-${text}`}
    onClick={onClick}
    sx={{
      '&': {
        ':hover': {
          backgroundColor: colord(color).invert().alpha(active ? 0.4 : 0.1).toRgbString(),
        },
        backgroundColor: colord(color).invert().alpha(active ? 0.4 : 0).toRgbString(),
        color: colord(color).toRgbString(),
      },
    }}
  >
    {text}
  </Typography>
)

const DefaultFormatterConfig = { desiredLength: 7 }

const Container = ({
  theme,
  timeFrame = 'one_hour',
  tokenA,
  tokenB,
  tz = 0,
  onCandleListUpdated,
  onPriceSentFromSocket,
  onTimeFrameChange,
}: PriceChartProps) => {
  const queryClient = useQueryClient()

  const [element, setElement] = useState<HTMLElement | null>(null)
  const [span, setSpan] = useState(tickerTimeFrameToSeconds[timeFrame])
  const [tokenIn, tokenOut] = useMemo(() => orderTokensLikeBE(tokenA, tokenB), [tokenA, tokenB])

  const [changeRate] = useState(0)

  const { lastMessage } = usePriceSocket(tokenIn, tokenOut)

  const fetchCandleData = useCallback(async () => {
    const tickerData = await queryClient.fetchQuery(
      getQueryKeyTickers(tokenA, tokenB, span),
      () => getTickers(tokenA, tokenB, secondsToTickerTimeFrame[span]),
      getQueryOptionsTickers(span),
    )

    return tickerData.candles
  }, [queryClient, span, tokenA, tokenB])

  const { candles, updateLast } = useLiquidityChart(element, {
    decimals: tokenB.decimals,
    formatterConfig: DefaultFormatterConfig,
    load: fetchCandleData,
    span,
    theme,
    tokenPair: TokenPair.of(tokenA.symbol, tokenB.symbol),
    tz,
  })

  useEffect(() => {
    if (!lastMessage) return

    onPriceSentFromSocket?.(lastMessage.data)

    const lastCandle = mergeOrStartNewCandle(candles[candles.length - 1], lastMessage.data, span)
    updateLast(lastCandle)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage])

  useEffect(() => {
    onCandleListUpdated?.(candles)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candles])

  useEffect(() => {
    setSpan(tickerTimeFrameToSeconds[timeFrame])
  }, [timeFrame])

  const changeRateColor = useMemo(
    () =>
      changeRate > 0
        ? theme?.candleRisingColor || '#55F9C7'
        : changeRate < 0
        ? theme?.candleFallingColor || '#FF6701'
        : '',
    [changeRate, theme?.candleFallingColor, theme?.candleRisingColor],
  )

  return (
    <Stack className="w-full h-full" spacing={2}>
      <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            className="font-bold text-xl"
            data-testid="current-price"
            style={{ color: theme?.headerTextColor || theme?.textColor || '#FFFFFF' }}
          >
            {candles.length ? format(candles[candles.length - 1].close, tokenB.decimals) : 'N/A'}
          </Typography>
          <Typography
            className={cls(
              'bg-opacity-10 rounded-lg px-2 py-1 text-sm',
              changeRate === 0 && 'hidden',
            )}
            data-testid="current-change-rate"
            style={{
              background: colord(changeRateColor).alpha(0.1).toRgbString(),
              color: changeRateColor,
            }}
          >
            <NumericOutput decimals={2} sign value={changeRate * 100} />%
          </Typography>
        </Stack>
        <Stack className="select-none" direction="row" spacing={0.5}>
          <SpanOption
            active={span === 3600}
            color={theme?.headerTextColor || theme?.textColor || '#FFFFFF'}
            onClick={() => onTimeFrameChange?.('one_hour')}
            text="1H"
          />
          <SpanOption
            active={span === 14400}
            color={theme?.headerTextColor || theme?.textColor || '#FFFFFF'}
            onClick={() => onTimeFrameChange?.('four_hours')}
            text="4H"
          />
          <SpanOption
            active={span === 86400}
            color={theme?.headerTextColor || theme?.textColor || '#FFFFFF'}
            onClick={() => onTimeFrameChange?.('twenty_four_hour')}
            text="1D"
          />
          <SpanOption
            active={span === 604800}
            color={theme?.headerTextColor || theme?.textColor || '#FFFFFF'}
            onClick={() => onTimeFrameChange?.('one_week')}
            text="1W"
          />
        </Stack>
      </Stack>
      <Box className="flex-grow relative">
        <Box className="absolute inset-0" data-testid="chart" ref={setElement}></Box>
      </Box>
    </Stack>
  )
}

export default Container
